import { useEffect } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";

import type { Budget, BudgetFormData, Company } from "~/types/contabilidad";

const budgetSchema = z.object({
  company_id: z.string().min(1, "Selecciona una empresa"),
  fiscal_year: z.number().int().min(2000).max(2100),
  name: z.string().min(1, "Ingresa un nombre"),
  notes: z.string().optional(),
});

type BudgetSchemaData = z.infer<typeof budgetSchema>;

interface PresupuestoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companies: Company[];
  budget?: Budget;
  onSubmit: (data: BudgetFormData) => void;
  isLoading: boolean;
}

export function PresupuestoDialog({
  open,
  onOpenChange,
  companies,
  budget,
  onSubmit,
  isLoading,
}: PresupuestoDialogProps) {
  const currentYear = dayjs().year();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  const form = useForm<BudgetSchemaData>({
    resolver: zodResolver(budgetSchema),
    defaultValues: {
      company_id: "",
      fiscal_year: currentYear,
      name: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        company_id: budget?.company_id ?? "",
        fiscal_year: budget?.fiscal_year ?? currentYear,
        name: budget?.name ?? `Presupuesto ${currentYear}`,
        notes: budget?.notes ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, budget]);

  const handleSubmit = (data: BudgetSchemaData) => {
    // Build an explicit BudgetFormData: zod's z.infer marks every field
    // optional under this project's strictNullChecks:false, but the resolver
    // has already validated these are present.
    onSubmit({
      company_id: data.company_id ?? "",
      fiscal_year: data.fiscal_year ?? currentYear,
      name: data.name ?? "",
      notes: data.notes,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {budget ? "Editar Presupuesto" : "Nuevo Presupuesto"}
          </DialogTitle>
          <DialogDescription>
            {budget
              ? "Modifica el nombre o las notas del presupuesto."
              : "Puedes crear varios presupuestos (escenarios) por empresa y año, cada uno con un nombre distinto."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="company_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Empresa</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!!budget}
                  >
                    <FormControl>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Selecciona una empresa" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="fiscal_year"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Año</FormLabel>
                  <Select
                    value={field.value.toString()}
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    disabled={!!budget}
                  >
                    <FormControl>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Selecciona un año" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {yearOptions.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ej: Presupuesto 2026"
                      className="h-9"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas (opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Notas o supuestos del presupuesto..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {budget ? "Guardar" : "Crear Presupuesto"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
